import cx from 'classnames';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Tooltip, Icon} from 'components';
import {PaymentSourceStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformPaymentSource} from 'types';
import {transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.PAYMENT_SOURCE;

type Props = {
    paymentSource: BillingPlatformPaymentSource;
    iconPlacement?: 'left' | 'right'
    className?: string;
}

export const PaymentSourceExpiry: React.FC<Props> = ({paymentSource, iconPlacement, className}) => {
    const {t} = useTranslation();

    const iconClassName: string = useMemo(() => {
        if (iconPlacement) {
            return iconPlacement === 'right' ? 'tw-order-last' : '';
        }

        return '-sm:tw-order-last';
    }, [iconPlacement]);

    const renderWarningIcon = useCallback(() => {
        if (paymentSource.status === PaymentSourceStatus.Expired) {
            return (
                <Tooltip className={cx(iconClassName, 'tw-mx-2')} header={t(T_KEYS.CARD.TOOLTIP.EXPIRED)}>
                    <Icon name='alert-triangle' color='alert' />
                </Tooltip>
            );
        } else if (paymentSource.status === PaymentSourceStatus.Expiring) {
            return (
                <Tooltip className={cx(iconClassName, 'tw-mx-2')} header={t(T_KEYS.CARD.TOOLTIP.EXPIRES_SOON)}>
                    <Icon name='alert-triangle' color='warning' />
                </Tooltip>
            );
        }
        return null;
    }, [paymentSource, iconClassName, t]);

    return (
        <div className={cx('tw-flex tw-items-center sm:tw-ml-auto', className, {
            'tw-text-alert': paymentSource.status === PaymentSourceStatus.Expired
        })}
        >
            {renderWarningIcon()}
            {paymentSource.status === PaymentSourceStatus.Expired ?
                <div className='tw-mr-1'>{t(T_KEYS.CARD.EXPIRED)}</div> :
                <Fragment>
                    <div className='tw-mr-1'>{t(T_KEYS.CARD.EXPIRES)}</div>
                    <div className='tw-font-bold'>{transformUtils.formatCreditCardExpiry(paymentSource.card)}</div>
                </Fragment>}
        </div>
    );
};